// frontend/src/services/invitationService.js

import { API } from '../config/apiPaths';
const BASE_URL = process.env.REACT_APP_API_BASE;

/**
 * Elimina una invitación de un grupo.
 * @param {string} invitationHash - Hash de la invitación (guest).
 * @param {string} leaderHash - Hash del líder (x-access-hash).
 * @returns {Promise<object>} Respuesta del backend.
 * @throws {Error} Si la operación falla.
 */
export async function deleteInvitation(invitationHash, leaderHash) {
  const res = await fetch(
    `${BASE_URL}${API.invitations.delete(invitationHash)}`,
    {
      method: 'DELETE',
      headers: { 'x-access-hash': leaderHash },
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || 'Error deleting invitation');
  }

  return res.json();
}

/**
 * Fetches invitations for a group with pagination.
 * @param {string} sessionHash - The group's session hash.
 * @param {number} [page=1] - The page number to fetch.
 * @param {number} [limit=20] - Items per page.
 * @returns {Promise<object>} Invitations and pagination metadata.
 * @throws {Error} If the request fails.
 */
export async function getInvitations(sessionHash, page = 1, limit) {
  const url = new URL(`${BASE_URL}${API.invitations.list()}`);

  // Avoid sending invalid values (e.g., limit=null / NaN)
  const p = Number(page);
  const l = Number(limit);
  if (Number.isFinite(p) && p > 0) url.searchParams.append('page', String(p));
  if (Number.isFinite(l) && l > 0) url.searchParams.append('limit', String(l));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-access-hash': sessionHash,
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errorMsg = data?.error || 'Failed to fetch invitations.';
    throw new Error(errorMsg);
  }

  return data;
}

/**
 * Cambia el estado de una invitación específica.
 * @param {string} invitationHash - Hash de la invitación (guest).
 * @param {'active'|'blocked'|'revoked'} status - Nuevo estado.
 * @param {string} hash - Hash del líder (x-access-hash).
 * @returns {Promise<object>} Respuesta del backend.
 * @throws {object} Error serializado del backend.
 */
export async function updateInvitationStatus(
  invitationHash,
  status,
  hash
) {
  const res = await fetch(
    `${BASE_URL}${API.invitations.status(invitationHash)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-access-hash': hash,
      },
      body: new URLSearchParams({ status }),
    }
  );

  if (!res.ok) {
    const error = await res.json().catch(() => ({
      error: 'Error inesperado',
    }));
    throw error;
  }

  return res.json();
}
